import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import TwoFactorService from '~/modules/services/two-factor-service';

export const TWO_FACTOR_QUERY_KEY = ['two-factor', 'status'];

/**
 * The authenticator-app setup flow, as a small state machine.
 *
 * The steps exist because the secret and the recovery codes are each readable exactly once, in
 * one response, and are never fetchable again. That makes this flow unlike an ordinary form: the
 * component cannot re-render itself from server state half way through, so the responses are held
 * here until the person has finished with them and `reset()` drops them.
 *
 * - `idle` — nothing set up, or set up and done
 * - `secret` — a secret has been issued and is on screen as a QR code, waiting for a code
 * - `codes` — setup finished; the recovery codes are on screen and have to be acknowledged
 */
export const STEPS = {
	IDLE: 'idle',
	SECRET: 'secret',
	CODES: 'codes'
};

/**
 * Just what the account has, for anything that needs to render the state without driving the
 * setup flow.
 *
 * Separate so the section heading and the method row below it can each ask, and share the one
 * request: react-query dedupes on the key, so two callers cost one fetch.
 */
export function useTwoFactorStatus() {
	const { data: status, isLoading: isLoadingStatus } = useQuery({
		queryKey: TWO_FACTOR_QUERY_KEY,
		queryFn: TwoFactorService.status,
		// The secret is not in here, so there is nothing sensitive to keep fresh -- but a stale
		// "no authenticator" would offer setup to somebody who just finished it in another tab.
		staleTime: 0
	});

	return { status, isLoadingStatus };
}

export default function useTwoFactor() {
	const queryClient = useQueryClient();

	const [step, setStep] = useState(STEPS.IDLE);
	const [enrollment, setEnrollment] = useState(null);
	const [recoveryCodes, setRecoveryCodes] = useState(null);
	const [error, setError] = useState(null);

	const { status, isLoadingStatus } = useTwoFactorStatus();

	const refreshStatus = () => queryClient.invalidateQueries({ queryKey: TWO_FACTOR_QUERY_KEY });

	const { mutateAsync: enroll, isLoading: isEnrolling } = useMutation({
		mutationFn: TwoFactorService.enroll,
		onMutate: () => setError(null),
		onSuccess: (data) => {
			setEnrollment(data);
			setStep(STEPS.SECRET);
		},
		onError: (e) => setError(e?.message || 'Could not start setup')
	});

	const { mutateAsync: confirm, isLoading: isConfirming } = useMutation({
		mutationFn: TwoFactorService.confirm,
		onMutate: () => setError(null),
		onSuccess: (data) => {
			setRecoveryCodes(data?.recoveryCodes || []);
			// Dropped as soon as it is no longer needed: the secret has done its job once the
			// authenticator holds it, and there is no reason for it to stay in memory while the
			// person copies ten recovery codes down.
			setEnrollment(null);
			setStep(STEPS.CODES);
			refreshStatus();
		},
		onError: (e) => setError(e?.message || 'Could not complete setup')
	});

	const { mutateAsync: disable, isLoading: isDisabling } = useMutation({
		mutationFn: TwoFactorService.disable,
		onMutate: () => setError(null),
		onSuccess: () => {
			setStep(STEPS.IDLE);
			setEnrollment(null);
			setRecoveryCodes(null);
			refreshStatus();
		},
		onError: (e) => setError(e?.message || 'Could not turn off two-factor authentication')
	});

	const { mutateAsync: replaceRecoveryCodes, isLoading: isReplacingCodes } = useMutation({
		mutationFn: TwoFactorService.replaceRecoveryCodes,
		onMutate: () => setError(null),
		onSuccess: (data) => {
			setRecoveryCodes(data?.recoveryCodes || []);
			setStep(STEPS.CODES);
			refreshStatus();
		},
		onError: (e) => setError(e?.message || 'Could not issue new recovery codes')
	});

	/**
	 * Leave the flow, forgetting whatever it was holding.
	 *
	 * Called when the person closes the dialog or acknowledges their codes. An abandoned
	 * enrollment is left unconfirmed on the server, which gates nothing and is replaced by the
	 * next attempt.
	 */
	const reset = () => {
		setStep(STEPS.IDLE);
		setEnrollment(null);
		setRecoveryCodes(null);
		setError(null);
	};

	return {
		status,
		isLoadingStatus,
		step,
		enrollment,
		recoveryCodes,
		error,
		setError,
		enroll,
		isEnrolling,
		confirm,
		isConfirming,
		disable,
		isDisabling,
		replaceRecoveryCodes,
		isReplacingCodes,
		reset,
		isBusy: isEnrolling || isConfirming || isDisabling || isReplacingCodes
	};
}
